import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subscriptionManager } from '@/utils/subscriptionManager';
import { inventoryKeys } from './useInventory';

interface RealtimeTableConfig {
  /** Human friendly identifier for logging */
  name: string;
  /** Database table to subscribe to */
  table: string;
  /** Optional Postgres schema (defaults to public) */
  schema?: string;
  /** Optional column used to scope subscriptions per branch */
  filterColumn?: string;
}

const realtimeTables: RealtimeTableConfig[] = [
  { name: 'raw-materials', table: 'raw_materials', filterColumn: 'branch_id' },
  { name: 'inventory-transactions', table: 'inventory_transactions', filterColumn: 'branch_id' },
  { name: 'material-cost-history', table: 'material_cost_history', filterColumn: 'branch_id' },
  { name: 'inventory-snapshots', table: 'inventory_snapshots', filterColumn: 'branch_id' },
  { name: 'recipes', table: 'recipes', filterColumn: 'branch_id' },
  { name: 'recipe-versions', table: 'recipe_versions', filterColumn: 'branch_id' },
  // Note: expenses table uses user_id, not branch_id (allocated via branch_expenses table)
  { name: 'expenses', table: 'expenses', filterColumn: undefined }
];

/**
 * Realtime Supabase subscription manager for inventory data.
 *
 * When any of the tracked tables emit changes, we invalidate every
 * React Query cache entry that starts with the "inventory" key prefix.
 */
export function useInventoryRealtime(branchId?: string | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!branchId) {
      return;
    }

    const activeChannels = realtimeTables.map(({ name, table, schema = 'public', filterColumn }) => {
      const channelName = `inventory-${name}-${branchId}`;
      const filter = filterColumn && branchId ? `${filterColumn}=eq.${branchId}` : undefined;

      subscriptionManager.createChannel(
        channelName,
        {
          event: '*',
          schema,
          table,
          filter
        },
        () => {
          queryClient.invalidateQueries({ queryKey: inventoryKeys.all, exact: false });
        }
      );

      return channelName;
    });

    return () => {
      activeChannels.forEach((channelName) => subscriptionManager.removeChannel(channelName));
    };
  }, [branchId, queryClient]);
}
